// Where the widget keeps things, all in the visitor's browser on the host site's origin:
// - localStorage: the visitor's settings (API key, model, voice) and panel width. They last until
//   cleared. With "remember my key" off, the key goes to sessionStorage instead and is gone when
//   the tab closes.
// - sessionStorage: the conversation, whether the panel is open, and a turn that is crossing a
//   navigation. They last for this tab, so a chat follows the visitor from page to page and ends
//   when the tab closes, like the extension's per-tab chats.
// Storage can be unavailable (private mode, blocked site data): every access is guarded and the
// widget then simply forgets on reload.

const PREFIX = 'bcagent:';

function read(area, key, fallback) {
  try {
    const raw = window[area].getItem(PREFIX + key);
    return raw == null ? fallback : JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function write(area, key, value) {
  try {
    if (value === undefined) window[area].removeItem(PREFIX + key);
    else window[area].setItem(PREFIX + key, JSON.stringify(value));
    return true;
  } catch {
    return false; // quota or blocked: keep it in memory only
  }
}

/* ------------------------------ preferences ------------------------------ */

/** The visitor's settings. A site can change the model defaults with data-* attributes (see config.js). */
export const DEFAULTS = {
  apiKey: '',
  rememberKey: true, // false: keep the key for this tab only
  model: 'gpt-5.4-nano',
  baseUrl: 'https://api.openai.com/v1',
  reasoningEffort: '',
  maxSteps: 24,
  // voice input (speech to text)
  sttEnabled: false,
  sttHotkey: true, // hold a shortcut to talk
  sttKey: 'ctrl+space', // see TALK_KEYS in speech.js (Alt+Space can't work on Windows)
  sttAutoSend: true, // false: put the transcript in the box to review first
  sttModel: 'gpt-transcribe',
  sttLanguage: '', // optional hint, e.g. "en" or "en, fr"
  // voice output (text to speech)
  ttsEnabled: false,
  ttsWhen: 'always', // 'always' | 'voice' (only replies to spoken messages)
  ttsVoice: 'marin',
  ttsModel: 'gpt-4o-mini-tts',
  ttsSpeed: 1,
  ttsStyle: '', // optional delivery instructions (gpt-4o-mini-tts)
};

/** Settings: the defaults, then the site's (data-* attributes), then what the visitor saved. */
export function loadPrefs(siteDefaults = {}) {
  const base = { ...DEFAULTS };
  for (const [k, v] of Object.entries(siteDefaults)) if (k in DEFAULTS && typeof v === typeof DEFAULTS[k] && v !== '' && k !== 'apiKey') base[k] = v;
  const saved = read('localStorage', 'settings', {});
  const out = { ...base };
  for (const k of Object.keys(DEFAULTS)) if (saved && typeof saved[k] === typeof DEFAULTS[k]) out[k] = saved[k];
  if (!out.rememberKey) out.apiKey = read('sessionStorage', 'key', '') || '';
  return out;
}

export function savePrefs(patch, siteDefaults = {}) {
  const next = { ...loadPrefs(siteDefaults), ...patch };
  next.apiKey = String(next.apiKey || '').trim();
  next.rememberKey = next.rememberKey !== false;
  next.baseUrl = (String(next.baseUrl || '').trim() || DEFAULTS.baseUrl).replace(/\/+$/, '');
  next.model = String(next.model || '').trim() || siteDefaults.model || DEFAULTS.model;
  next.maxSteps = Math.max(1, Math.min(100, parseInt(next.maxSteps, 10) || DEFAULTS.maxSteps));
  next.sttModel = String(next.sttModel || '').trim() || DEFAULTS.sttModel;
  next.sttLanguage = String(next.sttLanguage || '').trim();
  next.ttsModel = String(next.ttsModel || '').trim() || DEFAULTS.ttsModel;
  next.ttsVoice = String(next.ttsVoice || DEFAULTS.ttsVoice).trim();
  next.ttsSpeed = Math.max(0.5, Math.min(2, Number(next.ttsSpeed) || 1));
  next.ttsStyle = String(next.ttsStyle || '').trim().slice(0, 300);
  // the key goes where the visitor chose: this browser, or this tab only
  write('localStorage', 'settings', { ...next, apiKey: next.rememberKey ? next.apiKey : '' });
  write('sessionStorage', 'key', next.rememberKey || !next.apiKey ? undefined : next.apiKey);
  return next;
}

export const loadWidth = () => read('localStorage', 'width', null);
export const saveWidth = (w) => write('localStorage', 'width', Math.round(w));

/* ------------------------------ this tab ------------------------------ */

export const loadHistory = (site) => {
  const h = read('sessionStorage', `chat:${site}`, []);
  return Array.isArray(h) ? h : [];
};
export const saveHistory = (site, history) => write('sessionStorage', `chat:${site}`, history);

export const loadOpen = () => read('sessionStorage', 'open', null);
export const saveOpen = (open) => write('sessionStorage', 'open', !!open);

/**
 * A turn that navigates leaves a note for the next page: { at, viaVoice, keys }.
 * `keys` name the navigations already made in this turn, so one that doesn't land can't loop.
 */
export function saveResume(site, note) {
  return write('sessionStorage', `resume:${site}`, { ...note, at: Date.now() });
}

/** Read and clear the note. Only a fresh one counts: an old one belongs to a tab restored much later. */
export function takeResume(site, maxAgeMs = 90000) {
  const note = read('sessionStorage', `resume:${site}`, null);
  write('sessionStorage', `resume:${site}`, undefined);
  if (!note || typeof note.at !== 'number' || Date.now() - note.at > maxAgeMs) return null;
  return note;
}
